/**
 * Catalog run helpers over product counts and product types: the run-product statuses in reading
 * order, products per status, and the product types a catalog run can choose to write.
 */
import type { PromptFlowGroup } from '../types/prompts.types';
import type { RunProductCountsRead } from '../types/run-progress.types';

import { humanizeEnum } from './run-status';

/** Products detection placed in no vertical — always written with the general instructions. */
export const NO_MATCH = 'NO_MATCH';

/** The shared body every vertical falls back to; a prompt directory, never a detected type. */
export const COMMON_VERTICAL = 'COMMON';

/** Run-product statuses in reading order: written, partly, failed, then the ones never written. */
const STATUS_ORDER = [
  'ENRICHED',
  'PARTIAL',
  'FAILED',
  'NOT_ATTEMPTED',
  'SKIPPED_NOT_SELECTED',
  'SKIPPED_NO_PROMPT',
  'NOT_REQUESTED',
];

/** Statuses in display order — the known ones first, then any newer value the service sends. */
export function orderedRunProductStatuses(statuses: Iterable<string>): string[] {
  const set = new Set(statuses);
  const known = STATUS_ORDER.filter((status) => set.has(status));
  return [...known, ...[...set].filter((status) => !STATUS_ORDER.includes(status)).sort()];
}

/** Products per status, summed over the verticals. */
export function countsByStatus(
  counts: RunProductCountsRead | null | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of counts?.counts ?? []) {
    map.set(row.status, (map.get(row.status) ?? 0) + row.products);
  }
  return map;
}

/**
 * The product types a catalog run can choose to write: every vertical the writing step has
 * instructions for (from the active `CONTENT_ENRICHMENT` prompts), most products last run first.
 */
export function enrichableVerticals(
  group: PromptFlowGroup | undefined,
  lastRunCounts: ReadonlyMap<string, number>,
): string[] {
  const verticals = [...new Set((group?.versions ?? []).map((row) => row.vertical))].filter(
    (vertical) => vertical !== COMMON_VERTICAL && vertical !== '' && vertical !== NO_MATCH,
  );
  return verticals.sort(
    (a, b) =>
      (lastRunCounts.get(b) ?? 0) - (lastRunCounts.get(a) ?? 0) ||
      humanizeEnum(a).localeCompare(humanizeEnum(b)),
  );
}
